import { useState, useEffect } from 'react'
import { Clock, Zap, Sun, Info, Save, Loader2 } from 'lucide-react'
import RRToggle from '@/components/rr/RRToggle'
import { rrGetWorkingHours, rrUpdateWorkingHours, rrGetPriorityThresholds, rrUpdatePriorityThresholds } from '@/actions/rr'

type Tab = 'hours' | 'priority'
interface WorkingWindow { opens: string; closes: string; enabled: boolean }
type BucketWindows = { abc: WorkingWindow; reorder: WorkingWindow; rto: WorkingWindow }

function timeToPercent(t: string) {
  const [h, m] = t.split(':').map(Number)
  return ((h * 60 + m) / 1440) * 100
}

function WorkingHoursCard({ label, cls, window, onChange }: { label: string; cls: string; window: WorkingWindow; onChange: (w: WorkingWindow) => void }) {
  const startPct = timeToPercent(window.opens)
  const endPct = timeToPercent(window.closes)
  const TICKS = ['12a', '6a', '12p', '6p', '12a']
  return (
    <div className={`bg-white border border-gray-200 rounded-xl p-4 shadow-sm ${!window.enabled ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between mb-4">
        <span className={`text-xs font-extrabold px-2.5 py-1 rounded-lg ${cls}`}>{label}</span>
        <RRToggle on={window.enabled} onChange={val => onChange({ ...window, enabled: val })} />
      </div>
      <div className="flex items-end gap-2.5">
        <div className="flex-1">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Opens</label>
          <div className="flex items-center gap-2 h-10 border border-gray-200 rounded-lg px-3">
            <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <input type="time" value={window.opens} onChange={e => onChange({ ...window, opens: e.target.value })} className="flex-1 text-sm font-bold text-gray-900 border-0 outline-none bg-transparent" />
          </div>
        </div>
        <span className="text-gray-300 mb-2.5 font-bold">→</span>
        <div className="flex-1">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Closes</label>
          <div className="flex items-center gap-2 h-10 border border-gray-200 rounded-lg px-3">
            <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <input type="time" value={window.closes} onChange={e => onChange({ ...window, closes: e.target.value })} className="flex-1 text-sm font-bold text-gray-900 border-0 outline-none bg-transparent" />
          </div>
        </div>
      </div>
      <div className="mt-3.5">
        <div className="h-2 rounded-full bg-gray-100 relative overflow-hidden">
          <div className="absolute top-0 bottom-0 rounded-full" style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%`, background: 'linear-gradient(90deg, #157a4e, #0f5f3c)' }} />
        </div>
        <div className="flex justify-between mt-1">
          {TICKS.map((t, i) => <span key={i} className="text-[10px] text-gray-400">{t}</span>)}
        </div>
      </div>
    </div>
  )
}

const TIER_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  vh: { bg: 'bg-red-50',    text: 'text-red-600',    dot: 'bg-red-500' },
  h:  { bg: 'bg-orange-50', text: 'text-orange-600', dot: 'bg-orange-500' },
  m:  { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-400' },
  l:  { bg: 'bg-gray-100',  text: 'text-gray-500',   dot: 'bg-gray-400' },
}

function PriorityBadgeSmall({ colorKey, label }: { colorKey: string; label: string }) {
  const c = TIER_COLORS[colorKey]
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-bold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />{label}
    </span>
  )
}

const BUCKET_API_KEY: Record<'abc' | 'reorder' | 'rto', string> = { abc: 'abandoned_cart', reorder: 'reorder', rto: 'rto' }

export default function HoursPage() {
  const [tab, setTab] = useState<Tab>('hours')
  const [windows, setWindows] = useState<BucketWindows>({
    abc:     { opens: '08:00', closes: '20:00', enabled: true },
    reorder: { opens: '08:00', closes: '20:00', enabled: true },
    rto:     { opens: '08:00', closes: '20:00', enabled: true },
  })
  // thresholds[bucket] = { very_high, high, medium }
  const [thresholds, setThresholds] = useState<Record<string, { very_high: string; high: string; medium: string }>>({
    abandoned_cart: { very_high: '2', high: '5', medium: '12' },
    reorder:        { very_high: '0', high: '24', medium: '72' },
    rto:            { very_high: '2', high: '5', medium: '12' },
  })
  const [loading, setLoading] = useState(true)
  const [savingHours, setSavingHours] = useState(false)
  const [savingThresholds, setSavingThresholds] = useState(false)

  useEffect(() => {
    Promise.all([rrGetWorkingHours(), rrGetPriorityThresholds()]).then(([wh, pt]) => {
      interface WhRow { bucket: string; opens: string; closes: string; is_enabled: boolean }
      interface PtRow { bucket: string; very_high_threshold: number; high_threshold: number; medium_threshold: number }
      const whData: WhRow[] = (wh?.data ?? []) as WhRow[]
      const newWin: Partial<BucketWindows> = {}
      for (const row of whData) {
        const key = row.bucket === 'abandoned_cart' ? 'abc' : row.bucket as 'reorder' | 'rto'
        newWin[key] = { opens: row.opens, closes: row.closes, enabled: row.is_enabled }
      }
      setWindows(prev => ({ ...prev, ...newWin }))

      const ptData: PtRow[] = (pt?.data ?? []) as PtRow[]
      const newThresh: Record<string, { very_high: string; high: string; medium: string }> = { ...thresholds }
      for (const row of ptData) {
        newThresh[row.bucket] = {
          very_high: String(row.very_high_threshold),
          high: String(row.high_threshold),
          medium: String(row.medium_threshold),
        }
      }
      setThresholds(newThresh)
    }).finally(() => setLoading(false))
  }, [])

  async function saveHours() {
    setSavingHours(true)
    try {
      await rrUpdateWorkingHours(
        (Object.entries(BUCKET_API_KEY) as ['abc' | 'reorder' | 'rto', string][]).map(([k, apiKey]) => ({
          bucket: apiKey, opens: windows[k].opens, closes: windows[k].closes, is_enabled: windows[k].enabled,
        }))
      )
    } finally { setSavingHours(false) }
  }

  async function saveThresholds() {
    setSavingThresholds(true)
    try {
      await rrUpdatePriorityThresholds(
        Object.entries(thresholds).map(([bucket, t]) => ({
          bucket,
          very_high_threshold: parseFloat(t.very_high) || 0,
          high_threshold: parseFloat(t.high) || 0,
          medium_threshold: parseFloat(t.medium) || 0,
        }))
      )
    } finally { setSavingThresholds(false) }
  }

  const BUCKETS: { key: 'abc' | 'reorder' | 'rto'; label: string; cls: string }[] = [
    { key: 'abc',     label: 'ABC',     cls: 'bg-blue-50 text-blue-700'   },
    { key: 'reorder', label: 'Reorder', cls: 'bg-green-50 text-green-700' },
    { key: 'rto',     label: 'RTO',     cls: 'bg-red-50 text-red-700'     },
  ]

  if (loading) return <div className="flex justify-center items-center h-64"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>

  return (
    <div className="h-full flex flex-col">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-brandColor flex items-center justify-center flex-shrink-0">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">Working Hours & Priority</h1>
            <p className="text-sm text-gray-500 mt-0.5">Define when assignment runs and how lead urgency is calculated — both configured independently per bucket.</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="inline-flex gap-1 bg-gray-100 p-1 rounded-xl mb-5">
          <button onClick={() => setTab('hours')} className={`inline-flex items-center gap-2 h-9 px-4 rounded-lg text-sm font-semibold transition-all ${tab === 'hours' ? 'bg-white text-brandColor shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <Sun className="w-4 h-4" /> Working Hours
          </button>
          <button onClick={() => setTab('priority')} className={`inline-flex items-center gap-2 h-9 px-4 rounded-lg text-sm font-semibold transition-all ${tab === 'priority' ? 'bg-white text-brandColor shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <Zap className="w-4 h-4" /> Priority Thresholds
          </button>
        </div>

        {tab === 'hours' && (
          <>
            <div className="grid grid-cols-3 gap-4">
              {BUCKETS.map(b => (
                <WorkingHoursCard key={b.key} label={b.label} cls={b.cls} window={windows[b.key]} onChange={w => setWindows(prev => ({ ...prev, [b.key]: w }))} />
              ))}
            </div>
            <div className="mt-4 flex items-start gap-2 text-xs text-gray-400 bg-gray-50 border border-gray-200 rounded-xl p-3">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-300" />
              Outside a bucket's window, new leads queue and are held until it opens — nothing is lost.
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={saveHours} disabled={savingHours} className="h-9 px-4 bg-brandColor text-white rounded-lg text-sm font-semibold hover:bg-brand-hover inline-flex items-center gap-2 disabled:opacity-50">
                {savingHours ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save hours
              </button>
            </div>
          </>
        )}

        {tab === 'priority' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              {/* ABC card */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <div className="mb-3">
                  <div className="text-sm font-bold text-gray-900">
                    <span className="bg-blue-50 text-blue-700 text-xs font-extrabold px-2 py-0.5 rounded-md">ABC</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Hours elapsed since the event</div>
                </div>
                {(() => {
                  const t = thresholds['abandoned_cart']
                  if (!t) return null
                  return (
                    <div className="divide-y divide-gray-50">
                      {[
                        { key: 'very_high' as const, label: 'Very High', colorKey: 'vh', hint: '0 to' },
                        { key: 'high' as const,      label: 'High',      colorKey: 'h',  hint: 'up to' },
                        { key: 'medium' as const,    label: 'Medium',    colorKey: 'm',  hint: 'up to' },
                      ].map(row => (
                        <div key={row.key} className="flex items-center justify-between py-2.5">
                          <PriorityBadgeSmall colorKey={row.colorKey} label={row.label} />
                          <div className="flex items-center gap-1.5 text-xs text-gray-400">
                            <span>{row.hint}</span>
                            <input value={t[row.key]} onChange={e => setThresholds(p => ({ ...p, abandoned_cart: { ...p['abandoned_cart'], [row.key]: e.target.value } }))}
                              className="w-14 h-8 text-center border border-gray-200 rounded-lg text-xs font-bold text-gray-900 focus:outline-none focus:ring-1 focus:ring-brandColor" />
                            <span>h</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                })()}
              </div>

              {/* RTO card */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <div className="mb-3">
                  <div className="text-sm font-bold text-gray-900">
                    <span className="bg-red-50 text-red-700 text-xs font-extrabold px-2 py-0.5 rounded-md">RTO</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Hours elapsed since the event</div>
                </div>
                {(() => {
                  const t = thresholds['rto']
                  if (!t) return null
                  return (
                    <div className="divide-y divide-gray-50">
                      {[
                        { key: 'very_high' as const, label: 'Very High', colorKey: 'vh', hint: '0 to' },
                        { key: 'high' as const,      label: 'High',      colorKey: 'h',  hint: 'up to' },
                        { key: 'medium' as const,    label: 'Medium',    colorKey: 'm',  hint: 'up to' },
                      ].map(row => (
                        <div key={row.key} className="flex items-center justify-between py-2.5">
                          <PriorityBadgeSmall colorKey={row.colorKey} label={row.label} />
                          <div className="flex items-center gap-1.5 text-xs text-gray-400">
                            <span>{row.hint}</span>
                            <input value={t[row.key]} onChange={e => setThresholds(p => ({ ...p, rto: { ...p['rto'], [row.key]: e.target.value } }))}
                              className="w-14 h-8 text-center border border-gray-200 rounded-lg text-xs font-bold text-gray-900 focus:outline-none focus:ring-1 focus:ring-brandColor" />
                            <span>h</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                })()}
              </div>

              {/* Reorder card */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm col-span-2">
                <div className="mb-3">
                  <div className="text-sm font-bold text-gray-900"><span className="bg-green-50 text-green-700 text-xs font-extrabold px-2 py-0.5 rounded-md">Reorder</span></div>
                  <div className="text-xs text-gray-400 mt-1">Hours relative to reorder due date</div>
                </div>
                {(() => {
                  const t = thresholds['reorder']
                  if (!t) return null
                  return (
                    <div className="divide-y divide-gray-50">
                      {[
                        { key: 'very_high' as const, label: 'Very High', colorKey: 'vh', hint: '0 to' },
                        { key: 'high' as const,      label: 'High',      colorKey: 'h',  hint: 'up to' },
                        { key: 'medium' as const,    label: 'Medium',    colorKey: 'm',  hint: 'up to' },
                      ].map(row => (
                        <div key={row.key} className="flex items-center justify-between py-2.5">
                          <PriorityBadgeSmall colorKey={row.colorKey} label={row.label} />
                          <div className="flex items-center gap-1.5 text-xs text-gray-400">
                            <span>{row.hint}</span>
                            <input value={t[row.key]} onChange={e => setThresholds(p => ({ ...p, reorder: { ...p['reorder'], [row.key]: e.target.value } }))}
                              className="w-14 h-8 text-center border border-gray-200 rounded-lg text-xs font-bold text-gray-900 focus:outline-none focus:ring-1 focus:ring-brandColor" />
                            <span>h</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                })()}
              </div>

              {/* Live preview */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm col-span-2">
                <div className="flex items-center gap-2 text-sm font-extrabold text-gray-900 mb-4">
                  <Zap className="w-4 h-4 text-brandColor" /> Live preview
                </div>
                {[
                  { label: 'An ABC lead 3 hours old →',  tier: 'h',  name: 'High' },
                  { label: 'An RTO lead 30 min old →',    tier: 'vh', name: 'Very High' },
                  { label: 'A Reorder 24 hours overdue →',   tier: 'vh', name: 'Very High' },
                  { label: 'A Reorder 96 hours overdue →',  tier: 'l',  name: 'Low' },
                ].map(row => (
                  <div key={row.label} className="flex items-center justify-between py-2.5 border-t border-gray-200 first:border-0">
                    <span className="text-xs text-gray-600">{row.label}</span>
                    <PriorityBadgeSmall colorKey={row.tier} label={row.name} />
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 flex items-start gap-2 text-xs text-gray-400 bg-gray-50 border border-gray-200 rounded-xl p-3">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-300" />
              Changes take effect immediately — the priority of every existing lead recalculates the moment you save.
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={saveThresholds} disabled={savingThresholds} className="h-9 px-4 bg-brandColor text-white rounded-lg text-sm font-semibold hover:bg-brand-hover inline-flex items-center gap-2 disabled:opacity-50">
                {savingThresholds ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save thresholds
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
